import { config } from "../../config";
import { clamp } from "../../utils/math";
import { ACTION_NAMES } from "../../types/input";
import type {
  ActionName,
  InputSample,
  InputSource,
  InputState,
  ReadonlyInputState,
} from "../../types/input";

const createActions = (): Record<ActionName, boolean> => ({
  primary: false,
  secondary: false,
  jump: false,
});

/**
 * Agrège les sources et publie l'unique état lu par le reste du jeu.
 * Rien d'autre dans le projet ne touche à un événement clavier, souris ou
 * tactile : c'est ce qui rend desktop, mobile et (plus tard) manette
 * interchangeables.
 */
export class InputManager {
  private readonly sources: InputSource[] = [];

  /** Objets réutilisés d'une frame à l'autre : zéro allocation dans la boucle. */
  private readonly sample: InputSample = {
    move: { x: 0, y: 0 },
    moveDelta: { x: 0, y: 0 },
    lookDelta: { x: 0, y: 0 },
    zoomDelta: 0,
    actions: createActions(),
    pointer: { x: 0, y: 0 },
    pointerPresent: false,
    zoomAnchored: false,
    pick: false,
  };

  private readonly currentState: InputState = {
    move: { x: 0, y: 0 },
    look: { x: 0, y: 0 },
    zoom: 0,
    actions: createActions(),
    pointer: { x: 0, y: 0 },
    pointerPresent: false,
    zoomAnchored: false,
    pick: false,
  };

  get state(): ReadonlyInputState {
    return this.currentState;
  }

  addSource(source: InputSource): void {
    this.sources.push(source);
  }

  update(dt: number): void {
    this.resetSample();
    for (const source of this.sources) source.drain(this.sample, dt);

    const state = this.currentState;
    // Une direction poussée (clavier) et un glissement (deux doigts) se
    // cumulent : ce sont deux façons de demander la même chose.
    const moveScale = dt > 0 ? 1 / (dt * config.input.moveRateReference) : 0;
    state.move.x = clamp(
      this.sample.move.x + this.sample.moveDelta.x * moveScale,
      -1,
      1,
    );
    state.move.y = clamp(
      this.sample.move.y + this.sample.moveDelta.y * moveScale,
      -1,
      1,
    );

    // Les deltas bruts sont en pixels : les diviser par dt les transforme en
    // vitesse, donc en valeur stable quel que soit le framerate. Un
    // consommateur remultiplie par dt et retrouve le geste exact.
    const lookScale = dt > 0 ? 1 / (dt * config.input.lookRateReference) : 0;
    state.look.x = clamp(this.sample.lookDelta.x * lookScale, -1, 1);
    state.look.y = clamp(this.sample.lookDelta.y * lookScale, -1, 1);

    const zoomScale = dt > 0 ? 1 / (dt * config.input.zoomRateReference) : 0;
    state.zoom = clamp(this.sample.zoomDelta * zoomScale, -1, 1);

    for (const name of ACTION_NAMES)
      state.actions[name] = this.sample.actions[name];

    // Le pointeur ne se cumule pas : la dernière source à en fournir un
    // l'emporte, et il n'y en a qu'une à la fois en pratique.
    state.pointer.x = this.sample.pointer.x;
    state.pointer.y = this.sample.pointer.y;
    state.pointerPresent = this.sample.pointerPresent;
    state.zoomAnchored = this.sample.zoomAnchored;
    state.pick = this.sample.pick;
  }

  private resetSample(): void {
    this.sample.move.x = 0;
    this.sample.move.y = 0;
    this.sample.moveDelta.x = 0;
    this.sample.moveDelta.y = 0;
    this.sample.lookDelta.x = 0;
    this.sample.lookDelta.y = 0;
    this.sample.zoomDelta = 0;
    for (const name of ACTION_NAMES) this.sample.actions[name] = false;
    this.sample.pointerPresent = false;
    this.sample.zoomAnchored = false;
    this.sample.pick = false;
  }

  dispose(): void {
    for (const source of this.sources) source.dispose();
    this.sources.length = 0;
  }
}
